import { HandleType } from '@xyflow/react'
import { match } from 'ts-pattern'

import { getNodeHandleId } from '../helpers/nodeHandleId'
import { NodeDefinition, NodeType } from '../types'
import {
  ValidatorReturn,
  NodeValidatorContext,
  NodeValidatorFn,
  HandleValidatorFn
} from './types'
import {
  AutomationsPresentationV1Edge,
  AutomationsPresentationV1Node
} from 'Automations/automationEditor/types'

const isHandleConnected = (
  handleId: string,
  type: HandleType,
  edges: AutomationsPresentationV1Edge[]
): boolean => {
  return edges.some(edge =>
    match(type)
      .with('source', () => edge.sourceHandle === handleId)
      .with('target', () => edge.targetHandle === handleId)
      .exhaustive()
  )
}

const getHandleIds = ({
  nodeId,
  nodeData,
  type,
  definition
}: {
  nodeId: string
  nodeData: Record<string, unknown>
  type: HandleType
  definition: NodeDefinition<Record<string, unknown>>
}): string[] => {
  const handleMapping = type === 'source' ? definition.handleMapping : undefined

  if (!handleMapping) {
    return [getNodeHandleId({ nodeId, type })]
  }

  const mapping = handleMapping({ config: nodeData })

  return Object.keys(mapping).map(key =>
    getNodeHandleId({ nodeId, type, handleMapping: key })
  )
}

const getDownstreamNodeTypes = (
  nodeId: string,
  nodes: AutomationsPresentationV1Node[],
  edges: AutomationsPresentationV1Edge[],
  visited = new Set<string>()
): string[] => {
  if (visited.has(nodeId)) return []

  return edges
    .filter(e => e.source === nodeId)
    .flatMap(edge => {
      const target = nodes.find(n => n.id === edge.target)
      if (!target?.type) return []
      return [
        target.type,
        ...getDownstreamNodeTypes(
          target.id,
          nodes,
          edges,
          new Set([...visited, nodeId])
        )
      ]
    })
}

export const requiresDownstreamNode = ({
  nodeType,
  title,
  message
}: {
  nodeType: NodeType
  title: string
  message: string
}): NodeValidatorFn => {
  return ({ node, nodes, edges }): ValidatorReturn | undefined => {
    const downstreamTypes = getDownstreamNodeTypes(node.id, nodes, edges)
    return downstreamTypes.includes(nodeType) ? undefined : { title, message }
  }
}

/**
 * Flags a node that is the target of a forked handle — i.e. its upstream source
 * handle fans out to more than one edge, placing this node inside a parallel
 * fork. An end node cannot live on a forked branch (the generation pipeline
 * cannot express a terminate task inside a parallel fork). Reported at the node
 * level so it surfaces in the errors list without highlighting a handle.
 */
export const notOnForkedBranch = ({
  title,
  message
}: {
  title: string
  message: string
}): NodeValidatorFn => {
  return ({
    node,
    edges
  }: NodeValidatorContext): ValidatorReturn | undefined => {
    const incomingEdges = edges.filter(edge => edge.target === node.id)

    const isOnForkedBranch = incomingEdges.some(incomingEdge => {
      // Match the source node as well as the handle: a handle id is normally
      // globally unique, but presentations with an omitted/null sourceHandle
      // would otherwise group unrelated edges together (null === null) and
      // falsely flag the end node as forked.
      const edgesFromSameHandle = edges.filter(
        edge =>
          edge.source === incomingEdge.source &&
          edge.sourceHandle === incomingEdge.sourceHandle
      )
      return edgesFromSameHandle.length > 1
    })

    return isOnForkedBranch ? { title, message } : undefined
  }
}

export const allHandlesConnected = ({
  handleTypes,
  title,
  message
}: {
  handleTypes: HandleType[]
  title: string
  message: string
}): HandleValidatorFn => {
  return ({ node, definition, edges }: NodeValidatorContext) => {
    const nodeData = node.data as Record<string, unknown>

    return handleTypes
      .flatMap(type =>
        getHandleIds({ nodeId: node.id, nodeData, type, definition }).map(
          handleId => ({ handleId, type })
        )
      )
      .reduce<Record<string, ValidatorReturn>>((acc, { handleId, type }) => {
        if (isHandleConnected(handleId, type, edges)) return acc
        return { ...acc, [handleId]: { title, message } }
      }, {})
  }
}
